import React, { useState, useEffect, useCallback, useRef } from "react";
import { Flex } from "rebass/styled-components";
import { ProfileCard } from "./ProfileCard";
import { callApi } from "utils/ContentoApi";
import Loader from "components/common/loading/Loader";
import AddSourceModal from "./AddSourceModal";
import { SourceInput } from "../types";
import { useCreateGeneration } from "../useCreateGeneration";
import {
  InlineSubtitle,
  LeadIcon,
} from "../styles";

interface Profile {
    id: string;
    name: string;
    sourceType?: string;
    createdAt: string;
    updatedAt: string;
    content: string;
    customInstructions?: string | null;
    status: string;
    creatingBatchId?: string | null;
}

type ProfilesTabProps = {
  accountId: string
  isSourceModalOpen: boolean
  onCloseSourceModal: () => void
  expandProfileId?: string | null
  onProfilesChanged: () => void // parent keeps its own copy of the list (batch picker + profile limit)
}

const ProfilesTab: React.FC<ProfilesTabProps> = ({ accountId, isSourceModalOpen, onCloseSourceModal, expandProfileId, onProfilesChanged }) => {
  const { createProfile } = useCreateGeneration(accountId);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!expandProfileId) return;
    setExpandedIds((prev) => prev.includes(expandProfileId) ? prev : [...prev, expandProfileId]);
  }, [expandProfileId]);

  const fetchProfiles = useCallback(async (): Promise<Profile[]> => {
      const response = await callApi({
          method: "get",
          url: `content-factory/accounts/${accountId}/profiles`,
      }, true, true);
      return response?.profiles ?? [];
  }, [accountId]);

  // Expand a not-ready (generating or failed) profile the first time we see it so its
  // loader is visible by default — but only once, so a later manual collapse sticks.
  const expandedNonReadyIdsRef = useRef<Set<string>>(new Set());

  const fetchAndSetProfiles = useCallback(async () => {
      try {
          const fetched = await fetchProfiles();
          setProfiles(fetched);
          for (const profile of fetched) {
            if (profile.status !== "ready" && !expandedNonReadyIdsRef.current.has(profile.id)) {
              expandedNonReadyIdsRef.current.add(profile.id);
              setExpandedIds((prev) => prev.includes(profile.id) ? prev : [...prev, profile.id]);
            }
          }
      } catch (err) {
          console.error("Error fetching profiles", err);
      } finally {
          setLoading(false);
      }
  }, [fetchProfiles]);

  useEffect(() => {
      fetchAndSetProfiles();
  }, [fetchAndSetProfiles]);

  const toggleProfileExpansion = (id: string) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleProfileDeleted = useCallback((profileId: string) => {
    setProfiles((prev) => prev.filter((p) => p.id !== profileId));
    setExpandedIds((prev) => prev.filter((id) => id !== profileId));
    onProfilesChanged();
  }, [onProfilesChanged]);

  const handleProfileRenamed = useCallback((profileId: string, name: string) => {
    setProfiles((prev) => prev.map((p) => (p.id === profileId ? { ...p, name } : p)));
    onProfilesChanged();
  }, [onProfilesChanged]);

  const handleCreateProfileSubmit = async (source: SourceInput) => {
    const profileId = await createProfile(source);
    if (profileId) {
      await fetchAndSetProfiles();
      setExpandedIds((prev) => prev.includes(profileId) ? prev : [...prev, profileId]);
      onProfilesChanged();
    }
    onCloseSourceModal();
  };

  if (loading) {
      return (
        <div style={{ position: "relative", height: "200px" }}>
           <Loader location="center" size={48} />
        </div>
      );
  }

  return (
    <>
      <Flex alignItems="center" style={{ gap: "10px" }} mt={24} mb={12}>
        <LeadIcon size={15} />
        <InlineSubtitle>
          The business knowledge Willow writes from. Add a source and it learns
          your industry, audience and voice.
        </InlineSubtitle>
      </Flex>

      <Flex flexDirection="column" style={{ gap: "16px" }}>
        {profiles.map((profile) => (
          <ProfileCard
            key={profile.id}
            initialProfile={profile}
            isExpanded={expandedIds.includes(profile.id)}
            onToggleExpand={() => toggleProfileExpansion(profile.id)}
            onDeleted={() => handleProfileDeleted(profile.id)}
            onRenamed={(name) => handleProfileRenamed(profile.id, name)}
            accountId={accountId}
          />
        ))}
      </Flex>

      <AddSourceModal
        isOpen={isSourceModalOpen}
        onClose={onCloseSourceModal}
        onSubmit={handleCreateProfileSubmit}
        mode="profile"
      />
    </>
  );
};

export default ProfilesTab;
